import React from 'react';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import styled from 'styled-components';
import useWindowSize from '../../hooks/useWindowSize';
import getBreakpointFlags from '../../helpers/get-breakpoint-flags';
import isTouchDevice from '../../helpers/is-touch-device';
import Richtext from '../global/richtext/Richtext';
import Cursor from '../global/cursor/Cursor';
import style from './carousel.module.scss';
import CarouselModel from '../../models/carousel';
import Player from '../player/Player';
import { clampVW } from '../../helpers/clapm';
import { generateBlurDataURL } from '../../helpers/image-placeholders';

const SLIDES_PER_SIZE = { MOBILE: 1.3, TABLET: 3.5, DESKTOP: 3.75 };

type StyleProps = {
  isTouchDevice: boolean;
};

const CustomStyles = styled.div`
  .${style['carousel__icon-wrapper']} {
    opacity: ${(props: StyleProps) => {
      return props.isTouchDevice ? '1' : '0';
    }};
  }
`;

const getSwiperProps = (width: number, customSlides: any) => {
  const { isMobile, isTablet } = getBreakpointFlags(width);
  const spaceBetween = clampVW(12, 4, 60);
  let slidesPerView = null;
  const sliderOffset = clampVW(24, 5.5, 80);

  if (isMobile) {
    slidesPerView =
      customSlides && customSlides.MOBILE
        ? customSlides.MOBILE
        : SLIDES_PER_SIZE.MOBILE;
  } else if (isTablet) {
    slidesPerView =
      customSlides && customSlides.TABLET
        ? customSlides.TABLET
        : SLIDES_PER_SIZE.TABLET;
  } else {
    slidesPerView =
      customSlides && customSlides.DESKTOP
        ? customSlides.DESKTOP
        : SLIDES_PER_SIZE.DESKTOP;
  }

  return { slidesPerView, sliderOffset, spaceBetween };
};

type Props = {
  data: CarouselModel;
};

const Carousel = ({ data }: Props) => {
  const { tiles } = data;
  const { width } = useWindowSize();

  if (!data) return <></>;

  const { slidesPerView, sliderOffset, spaceBetween } = getSwiperProps(
    width,
    data.quantityTiles
  );

  const body = (
    <div className={style.carousel}>
      <Swiper
        loop={data.loop}
        spaceBetween={spaceBetween}
        slidesPerView={slidesPerView}
        slidesOffsetBefore={sliderOffset}
        slidesOffsetAfter={data.loop ? 0 : sliderOffset}
      >
        {tiles &&
          tiles.map(({ id, description, icon, image }) => (
            <SwiperSlide key={id}>
              {image?.isImage() && (
                <div className={style['carousel__image-wrapper']}>
                  <Image
                    placeholder="blur"
                    alt={image.alt}
                    src={image.src}
                    layout="responsive"
                    height={image.height}
                    width={image.width}
                    blurDataURL={generateBlurDataURL(image.src)}
                  />
                  {icon && (
                    <div className={style['carousel__icon-container']}>
                      <div className={style['carousel__icon-wrapper']}>
                        {icon && (
                          <Image
                            alt={icon.alt}
                            src={icon.src}
                            layout="responsive"
                            height={icon.height}
                            width={icon.width}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {image?.isVideo() && (
                <div className={style.carousel__videoWrapper}>
                  <Player
                    inlineMedia={image}
                    inlineClass={style.carousel__video}
                    fullscreenMedia={image}
                    aspectRatio="1/1"
                  />
                </div>
              )}
              {description && (
                <Richtext
                  data={description}
                  customClasses={{ paragraph: style.carousel__paragraph }}
                />
              )}
            </SwiperSlide>
          ))}
      </Swiper>
    </div>
  );

  return (
    <CustomStyles isTouchDevice={isTouchDevice()}>
      {data.cursor ? <Cursor data={data.cursor}>{body}</Cursor> : body}
    </CustomStyles>
  );
};

export default Carousel;
